import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar as CalendarIcon, Clock, MapPin, Users, Loader2, Plus, DoorOpen, Tent, X, CheckCircle2, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RecurrencePicker from "@/components/RecurrencePicker";
import { fmtDate } from "@/lib/format";

import RichContent from "@/components/RichContent";
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const friendly = (d) => d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const tomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); return d; };
const TIME_SLOTS = Array.from({ length: 29 }, (_, i) => {
  const mins = 7 * 60 + i * 30;
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
});
const fmt12 = (t) => {
  const [h, m] = String(t).split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
};
const statusBadge = (s) => s === "confirmed" || s === "approved" ? "bg-[#5C8A72]/15 text-[#5C8A72]"
  : s === "rejected" || s === "cancelled" ? "bg-red-100 text-red-600" : "bg-[#D98E73]/15 text-[#B5794E]";

export default function VenuePanel() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null); // { mode: 'hold'|'retreat', room }

  const rooms = useQuery({ queryKey: ["venue-rooms"], queryFn: () => api.get("/venue/rooms").then((r) => r.data) });
  const holds = useQuery({ queryKey: ["venue-my-holds"], queryFn: () => api.get("/enhancer/me/venue/holds").then((r) => r.data) });
  const retreats = useQuery({ queryKey: ["venue-my-retreats"], queryFn: () => api.get("/enhancer/me/venue/retreats").then((r) => r.data) });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["venue-my-holds"] });
    qc.invalidateQueries({ queryKey: ["venue-my-retreats"] });
  };

  const cancelHold = async (id) => {
    if (!window.confirm("Cancel this reservation?")) return;
    try { await api.delete(`/enhancer/venue/holds/${id}`); toast.success("Reservation cancelled"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const wellness = (rooms.data || []).filter((r) => !r.requires_approval);
  const retreatSpaces = (rooms.data || []).filter((r) => r.requires_approval);

  return (
    <div data-testid="enh-venue-panel">
      <div className="mb-6">
        <h2 className="font-serif text-2xl text-[#1A242B]">Schedule at EYW</h2>
        <p className="text-sm text-[#4A5D6B]">Reserve EYW wellness rooms for your appointments (instant when free), or propose a retreat in our larger spaces (center manager approval required).</p>
      </div>

      {/* Wellness rooms — instant */}
      <div className="mb-8">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[#144F75] uppercase tracking-wide mb-3"><DoorOpen size={16} /> Wellness rooms · instant confirmation</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {wellness.map((r) => (
            <RoomCard key={r.id} room={r} onReserve={() => setModal({ mode: "hold", room: r })} ctaLabel="Reserve room" />
          ))}
          {rooms.isLoading && <p className="text-sm text-[#4A5D6B]">Loading rooms…</p>}
          {!rooms.isLoading && wellness.length === 0 && <p className="text-sm text-[#4A5D6B]">No wellness rooms available.</p>}
        </div>
      </div>

      {/* Retreat spaces — approval */}
      <div className="mb-10">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[#B5794E] uppercase tracking-wide mb-3"><Tent size={16} /> Group & retreat spaces · Center manager approval</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {retreatSpaces.map((r) => (
            <RoomCard key={r.id} room={r} onReserve={() => setModal({ mode: "retreat", room: r })} ctaLabel="Propose retreat" accent />
          ))}
          {!rooms.isLoading && retreatSpaces.length === 0 && <p className="text-sm text-[#4A5D6B]">No retreat spaces available.</p>}
        </div>
      </div>

      {/* My reservations */}
      <div className="grid lg:grid-cols-2 gap-8">
        <div data-testid="enh-venue-holds">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3">My room reservations</h3>
          <div className="eyw-card divide-y divide-[#E5E0D8]">
            {(holds.data || []).map((h) => (
              <div key={h.id} className="p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-[#1A242B] flex items-center gap-2"><MapPin size={14} className="text-[#144F75]" /> {h.room_name}</div>
                  <div className="text-xs text-[#4A5D6B]">{fmtDate(h.date)} · {fmt12(h.start_time)}–{fmt12(h.end_time)}{h.title ? ` · ${h.title}` : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full ${statusBadge(h.status)}`}>{h.status}</span>
                  {h.status !== "cancelled" && h.status !== "rejected" && (
                    <button onClick={() => cancelHold(h.id)} data-testid={`venue-cancel-hold-${h.id}`} className="text-red-500 hover:text-red-600"><Trash2 size={15} /></button>
                  )}
                </div>
              </div>
            ))}
            {holds.data?.length === 0 && <p className="text-sm text-[#4A5D6B] p-4">No room reservations yet.</p>}
          </div>
        </div>

        <div data-testid="enh-venue-retreats">
          <h3 className="font-serif text-lg text-[#1A242B] mb-3">My retreats</h3>
          <div className="eyw-card divide-y divide-[#E5E0D8]">
            {(retreats.data || []).map((e) => (
              <div key={e.id} className="p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-[#1A242B] flex items-center gap-2"><Tent size={14} className="text-[#B5794E]" /> {e.title}</div>
                  <div className="text-xs text-[#4A5D6B]">{e.location} · {fmtDate(e.date)} · {fmt12(e.start_time)}–{fmt12(e.end_time)}</div>
                </div>
                <span className={`text-xs px-2.5 py-1 rounded-full ${statusBadge(e.approval_status)}`}>{e.approval_status === "approved" ? (e.published ? "published" : "approved") : e.approval_status}</span>
              </div>
            ))}
            {retreats.data?.length === 0 && <p className="text-sm text-[#4A5D6B] p-4">No retreats yet.</p>}
          </div>
        </div>
      </div>

      {modal && <ReserveModal mode={modal.mode} room={modal.room} onClose={() => setModal(null)} onSaved={refresh} />}
    </div>
  );
}

const RoomCard = ({ room, onReserve, ctaLabel, accent }) => (
  <div className="eyw-card p-5" data-testid={`venue-room-${room.key}`}>
    <div className="flex items-start justify-between">
      <h4 className="text-lg text-[#1A242B]">{room.name}</h4>
      <span className="text-xs text-[#4A5D6B] flex items-center gap-1"><Users size={13} /> {room.capacity}</span>
    </div>
    <RichContent preview text={room.description} className="text-xs text-[#4A5D6B] mt-1 min-h-[32px]" />
    <button onClick={onReserve} data-testid={`venue-reserve-${room.key}`}
      className={`mt-3 w-full ${accent ? "eyw-btn-ghost" : "eyw-btn"} !py-2 text-sm justify-center`}>
      <Plus size={15} /> {ctaLabel}
    </button>
  </div>
);

function ReserveModal({ mode, room, onClose, onSaved }) {
  const [dateObj, setDateObj] = useState(tomorrow());
  const [dateOpen, setDateOpen] = useState(false);
  const [start, setStart] = useState(mode === "retreat" ? "09:00" : "10:00");
  const [end, setEnd] = useState(mode === "retreat" ? "17:00" : "11:00");
  const [title, setTitle] = useState("");
  const [purpose, setPurpose] = useState("");
  const [capacity, setCapacity] = useState(room.capacity || 20);
  const [price, setPrice] = useState(0);
  const [recurrence, setRecurrence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null); // { needs_approval }
  const date = toISO(dateObj);

  const avail = useQuery({
    queryKey: ["venue-availability", room.key, date],
    queryFn: () => api.get(`/venue/availability?room_key=${room.key}&date=${date}`).then((r) => r.data),
  });
  const busy = avail.data?.busy || [];

  const conflicts = useMemo(() => {
    const toMin = (t) => { const [h, m] = String(t).split(":").map(Number); return h * 60 + m; };
    const s = toMin(start), e = toMin(end);
    return busy.some((b) => s < toMin(b.end) && toMin(b.start) < e);
  }, [busy, start, end]);

  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (start >= end) { setError("End time must be after start time."); return; }
    setLoading(true);
    try {
      if (mode === "hold") {
        const { data } = await api.post("/enhancer/venue/holds", { room_key: room.key, date, start_time: start, end_time: end, title, purpose });
        setDone({ needs_approval: data.needs_approval });
      } else {
        const { data } = await api.post("/enhancer/venue/retreats", {
          title: title || `${room.name} Retreat`, room_key: room.key, date,
          start_time: start, end_time: end, description: purpose, capacity: Number(capacity), price: Number(price),
          recurrence,
        });
        setDone({ needs_approval: data.needs_approval, occurrences_created: data.occurrences_created, occurrences_skipped: data.occurrences_skipped });
      }
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "object" ? (d.message || "Could not reserve.") : formatApiError(d) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm" data-testid="venue-reserve-modal">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-8 max-h-[92vh] overflow-y-auto eyw-reveal">
        <button onClick={onClose} data-testid="venue-modal-close" className="absolute top-4 right-4 text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
        {done ? (
          <div className="text-center py-8" data-testid="venue-reserve-success">
            <CheckCircle2 size={48} className="text-[#144F75] mx-auto mb-4" />
            <h3 className="font-serif text-2xl text-[#1A242B]">{done.needs_approval ? "Request submitted." : "Reserved."}</h3>
            <p className="text-[#4A5D6B] mt-2">
              {done.occurrences_created > 1
                ? `Created ${done.occurrences_created} recurring ${done.needs_approval ? "retreat requests" : "sessions"} for ${room.name}${done.occurrences_skipped?.length ? ` (${done.occurrences_skipped.length} skipped due to conflicts)` : ""}.${done.needs_approval ? " Pending center manager approval." : ""}`
                : done.needs_approval
                ? `Your ${mode === "retreat" ? "retreat" : "reservation"} for ${room.name} on ${friendly(dateObj)} is pending center manager approval. You'll be notified once it's reviewed.`
                : `${room.name} is booked for you on ${friendly(dateObj)}, ${fmt12(start)}–${fmt12(end)}.`}
            </p>
            <button onClick={onClose} className="eyw-btn mt-6">Done</button>
          </div>
        ) : (
          <>
            <p className="eyw-label mb-1">{mode === "retreat" ? "Propose a Retreat" : "Reserve a Room"}</p>
            <h3 className="font-serif text-2xl text-[#1A242B]">{room.name}</h3>
            <p className="text-sm text-[#4A5D6B] mt-1">
              {room.requires_approval ? "Requires center manager approval before it's confirmed." : "Confirms instantly when the room is free."}
            </p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <input placeholder={mode === "retreat" ? "Retreat title" : "Session title (optional)"} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="venue-title" className="eyw-input w-full" required={mode === "retreat"} />
              <div>
                <label className="text-xs text-[#4A5D6B] mb-1 block">Date</label>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" data-testid="venue-date" className="eyw-input w-full flex items-center justify-between text-left text-[#1A242B]">
                      <span>{friendly(dateObj)}</span><CalendarIcon size={16} className="text-[#8FA89B]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[110]" align="start">
                    <Calendar mode="single" selected={dateObj} onSelect={(d) => { if (d) { setDateObj(d); setDateOpen(false); } }}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">Start</label>
                  <Select value={start} onValueChange={setStart}>
                    <SelectTrigger data-testid="venue-start" className="eyw-input w-full h-auto py-3"><SelectValue>{fmt12(start)}</SelectValue></SelectTrigger>
                    <SelectContent className="z-[110] max-h-60">{TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{fmt12(t)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">End</label>
                  <Select value={end} onValueChange={setEnd}>
                    <SelectTrigger data-testid="venue-end" className="eyw-input w-full h-auto py-3"><SelectValue>{fmt12(end)}</SelectValue></SelectTrigger>
                    <SelectContent className="z-[110] max-h-60">{TIME_SLOTS.map((t) => <SelectItem key={t} value={t}>{fmt12(t)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {mode === "retreat" && (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-[#4A5D6B] mb-1 block">Capacity</label><input type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} data-testid="venue-capacity" className="eyw-input w-full" /></div>
                  <div><label className="text-xs text-[#4A5D6B] mb-1 block">Price ($)</label><input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="venue-price" className="eyw-input w-full" /></div>
                </div>
              )}

              {mode === "retreat" && (
                <div>
                  <label className="text-xs text-[#4A5D6B] mb-1 block">Repeat</label>
                  <RecurrencePicker value={recurrence} onChange={setRecurrence} testid="venue-recurrence" />
                </div>
              )}

              <textarea rows={2} placeholder={mode === "retreat" ? "Describe your retreat" : "Purpose (optional)"} value={purpose} onChange={(e) => setPurpose(e.target.value)} data-testid="venue-purpose" className="eyw-input w-full resize-none" />

              {/* Availability preview */}
              <div className="rounded-xl bg-white border border-[#E5E0D8] px-3 py-2.5" data-testid="venue-availability">
                <div className="text-xs font-semibold text-[#1A242B] flex items-center gap-1.5 mb-1"><Clock size={12} /> {friendly(dateObj)} — occupied</div>
                {avail.isLoading ? <p className="text-xs text-[#8FA89B]">Checking…</p>
                  : busy.length === 0 ? <p className="text-xs text-[#5C8A72]">Open all day — nothing booked.</p>
                  : <div className="flex flex-wrap gap-1.5">{busy.map((b, i) => <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-[#D98E73]/10 text-[#B5794E]">{fmt12(b.start)}–{fmt12(b.end)} {b.label}</span>)}</div>}
              </div>

              {conflicts && <p className="text-sm text-[#B5794E] bg-[#D98E73]/10 rounded-lg px-3 py-2" data-testid="venue-conflict-warning">Your selected time overlaps an existing booking. Please pick another slot.</p>}
              {error && <p className="text-sm text-red-600" data-testid="venue-error">{error}</p>}

              <button type="submit" disabled={loading || conflicts} data-testid="venue-submit" className="eyw-btn w-full disabled:opacity-60">
                {loading && <Loader2 className="animate-spin" size={18} />}
                {room.requires_approval ? "Submit for approval" : "Confirm reservation"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
